/* editor.jsx — WYSIWYG code editor + live render + AI build-assistant dock.
   Ported from the experimental editor branch (Viper handoff 2026-06-24) into the
   main Vite app. Same global-component pattern as every other screen: reads the
   bare `React` / `window.I` globals (globals.js + icons.jsx load first via
   main.jsx) and registers its components on `window.*` at the bottom — so
   review.jsx can gate on `typeof window.CodeWorkspace === 'function'` and degrade
   to the plain review when this module isn't present.

   - CodeEditor : CodeMirror core (window.CodeMirror via the CDN in index.html),
                  with a plain <textarea> fallback when CodeMirror is absent.
   - LivePreview: sandboxed iframe, re-renders the email as you type.
   - Dreamweaver-style two-way link: every major block carries data-sec.
       · click a block in the render → code scrolls to + highlights it
       · move the caret in the code  → the matching block outlines in the render
     (best-effort: only fires on builds whose HTML carries data-sec anchors — the
     placeholder seed does; real swarm HTML will once PY-12 has codex emit them.)
   - ChatDock  : bottom-of-page AI assistant. Calls window.GooseAPI.assistEdit
                 (the codex-CLI edit route — PY-12); degrades gracefully if absent. */

const { useState: useEdState, useEffect: useEdEffect, useRef: useEdRef } = React;

/* Seed build — a compact, real table-based email. Each major block is tagged
   data-sec="…" so the editor can map render ⇆ source both ways. Used as the
   editor's starting HTML when a build has no fetchable index.html (mock jobs /
   not-yet-built). For live builds, review.jsx swaps in the real index.html. */
function buildEmailHtml(client, accent, ink) {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width">
  <title>${client} — Q3 Activation</title>
</head>
<body style="margin:0;background:#f4f4f2;font-family:Arial,Helvetica,sans-serif;">
  <table role="presentation" width="100%" cellpadding="0" cellspacing="0" style="background:#f4f4f2;">
    <tr><td align="center" style="padding:24px 12px;">
      <table role="presentation" width="600" class="em_main_table" cellpadding="0" cellspacing="0" style="width:600px;max-width:600px;background:#ffffff;">
        <tr data-sec="bar"><td style="height:6px;background:${accent};font-size:0;line-height:0;">&nbsp;</td></tr>
        <tr data-sec="logo"><td style="padding:24px 28px 6px;">
          <div style="font-size:20px;font-weight:bold;color:${accent};letter-spacing:.5px;">${client}</div>
        </td></tr>
        <tr data-sec="hero"><td style="padding:6px 28px;">
          <div style="height:150px;background:#eceae3;border-radius:4px;text-align:center;line-height:150px;color:#9a9a92;font-size:12px;">hero.gif</div>
        </td></tr>
        <tr data-sec="headline"><td style="padding:18px 28px 4px;">
          <h1 style="margin:0;font-size:24px;line-height:30px;color:#1a1a1a;">Your Q3 upgrade is here</h1>
          <p style="margin:10px 0 0;font-size:15px;line-height:23px;color:#444444;">Switch today and lock in promotional pricing across every line on your account.</p>
        </td></tr>
        <tr data-sec="ratetable"><td style="padding:16px 28px;">
          <div style="height:90px;background:#f1efe8;border:1px solid #e3e0d6;border-radius:3px;text-align:center;line-height:90px;color:#a09a8a;font-size:12px;">rate_table.png</div>
        </td></tr>
        <tr data-sec="cta"><td align="center" style="padding:8px 28px 28px;">
          <a href="https://example.com/activate?utm_medium=email" style="display:inline-block;background:${accent};color:${ink};font-size:15px;font-weight:bold;text-decoration:none;padding:14px 32px;border-radius:4px;">Activate my offer</a>
        </td></tr>
        <tr data-sec="footer"><td style="padding:18px 28px;background:#f0f0ee;">
          <p style="margin:0;font-size:11px;line-height:16px;color:#8a8a8a;">You are receiving this because you opted in. ${client}, 123 Market St. Unsubscribe.</p>
        </td></tr>
      </table>
    </td></tr>
  </table>
</body>
</html>`;
}

function stripFences(s) {
  s = (s || '').trim();
  const m = s.match(/```(?:html|HTML)?\s*([\s\S]*?)```/);
  if (m) return m[1].trim();
  return s.replace(/^```(?:html)?\s*/i, '').trim();
}

/* Keep PY-12's window names while delegating the implementation to the canonical
   source-map helpers exposed by the house facade. */
function deriveSections(text) { return window.GooseReviewBlocks.deriveSections(text); }
function injectSections(text) { return window.GooseReviewBlocks.injectSections(text); }
function buildSectionMap(text) { return window.GooseReviewBlocks.buildSectionMap(text); }
function secLabel(sec, text) { return window.GooseReviewBlocks.secLabel(sec, text); }

function secAtLine(map, line) {
  const hit = map.find(s => line >= s.start && line <= s.end);
  return hit ? hit.sec : null;
}

/* ---------- CodeMirror editor ---------- */
function clearSearchMarks(inst) {
  if (!inst) return;
  (inst.__searchMarks || []).forEach((mark) => { try { mark.clear(); } catch (e) {} });
  inst.__searchMarks = [];
}

function markSearchMatches(inst, query, caseSensitive) {
  clearSearchMarks(inst);
  if (!inst || query.length < 2 || typeof inst.getSearchCursor !== 'function') return [];
  const cursor = inst.getSearchCursor(query, { line: 0, ch: 0 }, !caseSensitive);
  const matches = [];
  while (cursor.findNext()) {
    const from = cursor.from(), to = cursor.to();
    matches.push({ from, to });
    inst.__searchMarks.push(inst.markText(from, to, { className: 'cm-search-hit' }));
  }
  return matches;
}

function showSearchMatch(inst, match) {
  if (!inst || !match) return;
  inst.setSelection(match.from, match.to);
  inst.scrollIntoView({ from: match.from, to: match.to }, 80);
}

function CodeSearchBar({ available, open, inputRef, query, count, current, caseSensitive, onOpen, onQuery, onToggleCase, onMove, onClose }) {
  const unavailableTitle = 'search needs the code editor (CodeMirror unavailable)';
  if (!available) {
    return (
      <div className="cw-search is-disabled" data-editor-search="disabled" title={unavailableTitle} aria-disabled="true">
        <I.search size={13} />
        <input aria-label="Find in code" placeholder="Find in code" disabled title={unavailableTitle} />
        <span className="cw-search-count">0 of 0</span>
      </div>
    );
  }
  if (!open) {
    return <button className="btn btn-quiet btn-sm cw-search-open" data-editor-search="closed" onClick={onOpen} title="Find in code (Cmd/Ctrl-F)"><I.search size={13} /> Find</button>;
  }
  return (
    <div className="cw-search" data-editor-search="open" role="search">
      <I.search size={13} />
      <input ref={inputRef} aria-label="Find in code" placeholder="Find in code" value={query}
        onChange={(e) => onQuery(e.target.value)}
        onKeyDown={(e) => {
          if (e.key !== 'Enter') return;
          e.preventDefault();
          onMove(e.shiftKey ? -1 : 1);
        }} />
      <span className="cw-search-count" aria-live="polite">{count ? (current + 1) + ' of ' + count : '0 of 0'}</span>
      <button className={'cw-search-btn' + (caseSensitive ? ' on' : '')} onClick={onToggleCase} title="Match case" aria-label="Match case" aria-pressed={caseSensitive}>Aa</button>
      <button className="cw-search-btn" onClick={() => onMove(-1)} title="Previous match (Shift-Enter)" aria-label="Previous match"><I.chevron size={13} style={{ transform: 'rotate(180deg)' }} /></button>
      <button className="cw-search-btn" onClick={() => onMove(1)} title="Next match (Enter)" aria-label="Next match"><I.chevron size={13} /></button>
      <button className="cw-search-btn" onClick={onClose} title="Close find (Esc)" aria-label="Close find"><I.x size={13} /></button>
    </div>
  );
}

function CodeEditor({ value, onChange, cmRef, onCursorSec, onOpenSearch, onReady }) {
  const host = useEdRef(null);
  const cm = useEdRef(null);

  useEdEffect(() => {
    if (cm.current || !host.current || !window.CodeMirror) return;
    const inst = window.CodeMirror(host.current, {
      value: value,
      mode: 'htmlmixed',
      lineNumbers: true,
      lineWrapping: true,
      tabSize: 2,
      indentUnit: 2,
      autofocus: false,
    });
    inst.setSize('100%', '100%');
    const onInstChange = (i) => onChange(i.getValue());
    const onInstCursor = (i) => {
      if (!onCursorSec) return;
      const map = buildSectionMap(i.getValue());
      onCursorSec(secAtLine(map, i.getCursor().line));
    };
    const searchKeyMap = {
      'Cmd-F': () => onOpenSearch && onOpenSearch(),
      'Ctrl-F': () => onOpenSearch && onOpenSearch(),
    };
    inst.on('change', onInstChange);
    inst.on('cursorActivity', onInstCursor);
    if (inst.addKeyMap) inst.addKeyMap(searchKeyMap);
    cm.current = inst;
    if (cmRef) cmRef.current = inst;
    if (onReady) onReady(inst);
    return () => {
      clearSearchMarks(inst);
      if (inst.off) { inst.off('change', onInstChange); inst.off('cursorActivity', onInstCursor); }
      if (inst.removeKeyMap) inst.removeKeyMap(searchKeyMap);
      if (cmRef && cmRef.current === inst) cmRef.current = null;
      if (onReady) onReady(null);
      cm.current = null;
    };
  }, []);

  useEdEffect(() => {
    const inst = cm.current;
    if (inst && value !== inst.getValue()) {
      const c = inst.getCursor();
      inst.setValue(value);
      try { inst.setCursor(c); } catch (e) {}
    }
  }, [value]);

  if (!window.CodeMirror) {
    return <textarea className="cm-fallback" value={value} onChange={e => onChange(e.target.value)} spellCheck={false} />;
  }
  return <div className="cm-host" ref={host}></div>;
}

/* outline + scroll the active section's source in CodeMirror */
function markCodeSection(inst, sec, doScroll) {
  if (!inst) return;
  if (inst.__secMark) { inst.__secMark.clear(); inst.__secMark = null; }
  if (!sec) return;
  const map = buildSectionMap(inst.getValue());
  const hit = map.find(s => s.sec === sec);
  if (!hit) return;
  const lastLen = inst.getLine(hit.end) ? inst.getLine(hit.end).length : 0;
  inst.__secMark = inst.markText(
    { line: hit.start, ch: 0 },
    { line: hit.end, ch: lastLen },
    { className: 'cm-sec-active' }
  );
  if (doScroll) {
    inst.scrollIntoView({ line: hit.start, ch: 0 }, 80);
    inst.setCursor({ line: hit.start, ch: hit.startCh });
  }
}

/* ---------- live render (with section linking) ---------- */
const PREVIEW_LINK_CSS = `
  [data-sec]{cursor:pointer;transition:outline-color .12s,background-color .12s;}
  [data-sec]:hover{outline:2px dashed rgba(60,125,217,.55);outline-offset:-2px;}
  [data-sec].goose-active{outline:2px solid #3C7DD9 !important;outline-offset:-2px;background:rgba(60,125,217,.07);}
`;
function LivePreview({ html, device, activeSec, onPickSection, reloadKey, artifactUrl }) {
  const display = React.useMemo(
    () => window.GooseReviewBlocks.buildDisplayDoc(html, { artifactUrl }),
    [html, artifactUrl]
  );
  const ref = useEdRef(null);
  const cb = useEdRef({});
  cb.current = { activeSec, onPickSection };

  const applyActive = (doc) => {
    if (!doc) return;
    doc.querySelectorAll('[data-sec]').forEach(el => {
      el.classList.toggle('goose-active', el.getAttribute('data-sec') === cb.current.activeSec);
    });
  };
  const wire = () => {
    const f = ref.current; if (!f) return;
    let doc; try { doc = f.contentDocument; } catch (e) { return; }
    if (!doc || !doc.body) return;
    if (!doc.getElementById('goose-link-style')) {
      const st = doc.createElement('style');
      st.id = 'goose-link-style';
      st.textContent = PREVIEW_LINK_CSS;
      doc.head && doc.head.appendChild(st);
    }
    doc.body.onclick = (e) => {
      // emails contain real <a href> — never let a click navigate the preview
      // away (that would strand the WYSIWYG on a blank/blocked page).
      const a = e.target.closest && e.target.closest('a');
      if (a) { e.preventDefault(); e.stopPropagation(); }
      const t = e.target.closest && e.target.closest('[data-sec]');
      if (t && cb.current.onPickSection) cb.current.onPickSection(t.getAttribute('data-sec'));
    };
    applyActive(doc);
  };

  // (re)load srcdoc as html changes (or on an explicit reload); onload re-wires
  // + re-applies the active outline. reloadKey lets the toolbar re-render the
  // preview as a recovery if it ever gets navigated away.
  useEdEffect(() => {
    const f = ref.current; if (!f) return;
    const onLoad = () => wire();
    f.addEventListener('load', onLoad);
    const id = setTimeout(() => { try { f.srcdoc = display.doc; } catch (e) {} }, 200);
    return () => { clearTimeout(id); f.removeEventListener('load', onLoad); };
  }, [display.doc, reloadKey]);

  // active section changed without a reload → just retoggle classes
  useEdEffect(() => {
    try { applyActive(ref.current && ref.current.contentDocument); } catch (e) {}
  }, [activeSec]);

  return (
    <div className={'lp lp-' + device}>
      <div className="lp-screen">
        <iframe ref={ref} title="Live render" sandbox="allow-same-origin"></iframe>
      </div>
      {device === 'mobile' && <div className="lp-dim">375px</div>}
    </div>
  );
}

/* a self-contained fullscreen-toggle glyph (the shared I set has no maximize). */
function ExpandIcon({ size = 15, on = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      {on
        ? <path d="M8 3v3a2 2 0 0 1-2 2H3M21 8h-3a2 2 0 0 1-2-2V3M3 16h3a2 2 0 0 1 2 2v3M16 21v-3a2 2 0 0 1 2-2h3" />
        : <path d="M8 3H5a2 2 0 0 0-2 2v3M21 8V5a2 2 0 0 0-2-2h-3M3 16v3a2 2 0 0 0 2 2h3M16 21h3a2 2 0 0 0 2-2v-3" />}
    </svg>
  );
}

/* ---------- the split workspace ---------- */
function CodeWorkspace({ html, setHtml, onClose, fileName, jobId }) {
  const [device, setDevice] = useEdState('desktop');
  const [full, setFull] = useEdState(false);
  const saved = useEdRef(html);
  const [dirty, setDirty] = useEdState(false);
  const [saveState, setSaveState] = useEdState(null);   // null | 'saving' | 'saved' | 'not-persisted' | { error } — PY-40
  const [activeSec, setActiveSec] = useEdState(null);
  const [reloadKey, setReloadKey] = useEdState(0);
  const cmRef = useEdRef(null);
  const workspaceRef = useEdRef(null);
  const src = useEdRef('preview'); // who drove the last selection
  const searchInputRef = useEdRef(null);
  const searchMatches = useEdRef([]);
  const [searchReady, setSearchReady] = useEdState(false);
  const [searchOpen, setSearchOpen] = useEdState(false);
  const [searchQuery, setSearchQuery] = useEdState('');
  const [searchCount, setSearchCount] = useEdState(0);
  const [searchCurrent, setSearchCurrent] = useEdState(-1);
  const [searchCaseSensitive, setSearchCaseSensitive] = useEdState(false);
  const codeMirrorAvailable = typeof window.CodeMirror === 'function';
  const searchAvailable = codeMirrorAvailable && (!searchReady || typeof cmRef.current?.getSearchCursor === 'function');
  const artifactUrl = React.useMemo(() => (
    jobId && window.GooseAPI && typeof window.GooseAPI.artifactUrl === 'function'
      ? (relative) => window.GooseAPI.artifactUrl(jobId, relative)
      : null
  ), [jobId]);

  const onCode = (v) => { setHtml(v); setDirty(v !== saved.current); };
  const revert = () => { setHtml(saved.current); setDirty(false); };
  const openSearch = () => {
    if (!codeMirrorAvailable) return;
    setSearchOpen(true);
    setTimeout(() => { if (searchInputRef.current) { searchInputRef.current.focus(); searchInputRef.current.select(); } }, 0);
  };
  const closeSearch = () => {
    clearSearchMarks(cmRef.current);
    searchMatches.current = [];
    setSearchOpen(false); setSearchQuery(''); setSearchCount(0); setSearchCurrent(-1);
    setTimeout(() => { if (cmRef.current && cmRef.current.focus) cmRef.current.focus(); }, 0);
  };
  const moveSearch = (by) => {
    const matches = searchMatches.current;
    if (!matches.length) return;
    setSearchCurrent((current) => {
      const next = (current + by + matches.length) % matches.length;
      showSearchMatch(cmRef.current, matches[next]);
      return next;
    });
  };
  // PY-40: Save is real now — POST the edited HTML to disk (server does the atomic write +
  // pre-edit backup + manifest audit-stale + injection scan). A FAILURE keeps `dirty` set,
  // so an edit is NEVER silently lost. No jobId (mock/demo context) → local-only, tagged.
  const saveRoute = !!(jobId && window.GooseAPI && window.GooseAPI.saveArtifact);   // RW-08 (RW-04c): no route → nothing persists, say so
  const save = async () => {
    if (saveState === 'saving') return;
    if (!saveRoute) {
      setSaveState('not-persisted');   // LIVE-WIRE: no backend → not persisted
      setTimeout(() => setSaveState((s) => (s === 'not-persisted' ? null : s)), 4000);
      return;
    }
    setSaveState('saving');
    try {
      await window.GooseAPI.saveArtifact(jobId, html);
      saved.current = html; setDirty(false); setSaveState('saved');
      setTimeout(() => setSaveState((s) => (s === 'saved' ? null : s)), 2500);
    } catch (e) {
      const data = e && e.data;   // 422 injection → { error:'injection_blocked', findings:[…] }
      setSaveState({ error: (data && data.error) || (e && e.message) || 'Save failed', findings: data && data.findings });
    }
  };

  // a block was clicked in the render → scroll the code to it
  const pickFromPreview = (sec) => { src.current = 'preview'; setActiveSec(sec); };
  // the caret moved in the code → outline the block in the render (no code scroll)
  const pickFromCode = (sec) => { if (sec !== activeSec) { src.current = 'code'; setActiveSec(sec); } };

  // whenever the active section changes, keep the code marker in sync
  useEdEffect(() => {
    markCodeSection(cmRef.current, activeSec, src.current === 'preview');
  }, [activeSec]);

  // Search is viewer-side only: mark CodeMirror ranges after a short debounce,
  // then re-run against the live buffer after every edit. Old marks are always
  // cleared before a new set is installed, so repeated typing cannot leak marks.
  useEdEffect(() => {
    const inst = cmRef.current;
    if (!searchOpen || !searchReady || !inst) {
      clearSearchMarks(inst);
      searchMatches.current = [];
      setSearchCount(0); setSearchCurrent(-1);
      return;
    }
    const id = setTimeout(() => {
      const matches = markSearchMatches(inst, searchQuery, searchCaseSensitive);
      searchMatches.current = matches;
      setSearchCount(matches.length);
      setSearchCurrent(matches.length ? 0 : -1);
      if (matches.length) showSearchMatch(inst, matches[0]);
    }, 150);
    return () => clearTimeout(id);
  }, [searchOpen, searchReady, searchQuery, searchCaseSensitive, html]);

  // Esc closes only this workspace's open find bar. Capture + stopPropagation
  // keeps the same press from also tripping the fullscreen Escape handler below.
  useEdEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => {
      if (e.key !== 'Escape' || !workspaceRef.current || !workspaceRef.current.contains(e.target)) return;
      e.preventDefault(); e.stopPropagation(); closeSearch();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [searchOpen]);

  // Esc exits fullscreen (standard IDE/fullscreen behaviour).
  useEdEffect(() => {
    if (!full) return;
    const onKey = (e) => { if (e.key === 'Escape') setFull(false); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [full]);

  const workspace = (
    <div className={'cw card' + (full ? ' cw-full' : '')} ref={workspaceRef}>
      <div className="cw-bar">
        <div className="cw-bar-l">
          <div className="cw-tabs">
            <button className="cw-tab active" onClick={() => setReloadKey(k => k + 1)} title="Re-render the preview"><I.code size={13} /> {fileName}</button>
            {dirty && <span className="cw-dirty" title="Unsaved edits">●</span>}
          </div>
          <span className={'cw-link' + (activeSec ? ' on' : '')} title="Click a block in the render to jump to its code — or move the caret in the code to highlight the block.">
            <I.link size={12} />
            {activeSec
              ? <React.Fragment>Linked · <b>{secLabel(activeSec, html)}</b></React.Fragment>
              : <span className="cw-link-hint">Click any block to link code &#8646; render</span>}
          </span>
          <CodeSearchBar available={searchAvailable} open={searchOpen} inputRef={searchInputRef}
            query={searchQuery} count={searchCount} current={searchCurrent} caseSensitive={searchCaseSensitive}
            onOpen={openSearch} onQuery={setSearchQuery} onToggleCase={() => setSearchCaseSensitive((v) => !v)}
            onMove={moveSearch} onClose={closeSearch} />
        </div>
        <div className="cw-bar-r">
          <div className="tabs">
            <span className={'tab' + (device === 'desktop' ? ' active' : '')} onClick={() => setDevice('desktop')}>Desktop</span>
            <span className={'tab' + (device === 'mobile' ? ' active' : '')} onClick={() => setDevice('mobile')}>Mobile</span>
          </div>
          {dirty && <button className="btn btn-quiet btn-sm" onClick={revert}>Revert</button>}
          <button className="btn btn-primary btn-sm" onClick={save} disabled={!dirty || saveState === 'saving' || !saveRoute} title={saveRoute ? undefined : 'Not persisted — no save route in this lane; edits are applied through the Review workspace'}>
            {saveState === 'saving'
              ? <React.Fragment><span className="spinner"></span> Saving…</React.Fragment>
              : <React.Fragment><I.check size={13} /> Save</React.Fragment>}
          </button>
          {saveState === 'saved' &&
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--pass)' }} title="Written to disk">✓ Saved</span>}
          {saveState === 'not-persisted' &&
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--cond)' }} title="Not persisted — no save route in this lane; edits are applied through the Review workspace">⚠ Not persisted</span>}
          {saveState && saveState.error &&
            <span style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--fail)' }} title={saveState.error === 'injection_blocked' ? 'Blocked: the HTML contains an unsafe pattern (script/handler/iframe/…)' : saveState.error}>⚠ {saveState.error === 'injection_blocked' ? 'Blocked — unsafe HTML' : 'Save failed'}</span>}
          <button className="btn btn-quiet btn-sm" onClick={() => setFull(f => !f)} title={full ? 'Exit fullscreen (Esc)' : 'Fullscreen — make the page the IDE'}><ExpandIcon size={15} on={full} /></button>
          <button className="btn btn-quiet btn-sm" onClick={onClose} title="Close editor"><I.x size={15} /></button>
        </div>
      </div>
      <div className="cw-split">
        <div className="cw-pane cw-code">
          <CodeEditor value={html} onChange={onCode} cmRef={cmRef} onCursorSec={pickFromCode}
            onOpenSearch={openSearch} onReady={(inst) => setSearchReady(!!inst)} />
        </div>
        <div className="cw-gutter"></div>
        <div className="cw-pane cw-prev">
          <LivePreview html={html} device={device} activeSec={activeSec} onPickSection={pickFromPreview} reloadKey={reloadKey} artifactUrl={artifactUrl} />
        </div>
      </div>
    </div>
  );
  // Fullscreen portals to <body> so position:fixed resolves against the viewport,
  // not the review page (whose .fade-in transform is otherwise the containing
  // block and would pin the overlay to the page instead of the screen).
  return full ? ReactDOM.createPortal(workspace, document.body) : workspace;
}

/* ---------- AI build assistant (bottom dock) ----------
   Calls the codex-CLI edit route (window.GooseAPI.assistEdit, PY-12). The route
   hands the reviewer's instruction + the build's run-dir files to the codex
   engine, applies the edit in place, and returns the revised HTML. Until that
   route lands, GooseAPI.assistEdit is absent → an honest "wiring pending" reply
   (the code editor above still works for hand edits). */
function ChatDock({ html, setHtml, jobId, jobName, onApplied }) {
  const [open, setOpen] = useEdState(false);
  const [input, setInput] = useEdState('');
  const [busy, setBusy] = useEdState(false);
  const [msgs, setMsgs] = useEdState([
    { role: 'assistant', text: "Tell me what to change and I'll edit the build, then update the render. Try “make the CTA green”, “punch up the headline”, or “bump body copy to 16px”." },
  ]);
  const scroller = useEdRef(null);
  useEdEffect(() => { if (scroller.current) scroller.current.scrollTop = scroller.current.scrollHeight; }, [msgs, open]);

  const send = async (text) => {
    const req = (text || input).trim();
    if (!req || busy) return;
    setInput('');

    // The codex-CLI edit route (PY-12). Absent until wired → honest degrade; the
    // code editor above still works for direct hand edits in the meantime.
    const wired = window.GooseAPI && typeof window.GooseAPI.assistEdit === 'function';
    if (!wired) {
      setMsgs(m => [...m, { role: 'user', text: req }, { role: 'assistant', text: "The AI edit route isn't wired into this build yet (tracked as PY-12). Once it lands, this hands your change plus the build's files to the codex engine and applies the edit in place. For now, open the code editor above and edit the HTML directly — the live render updates as you type." }]);
      return;
    }

    setBusy(true);
    setMsgs(m => [...m, { role: 'user', text: req }, { role: 'assistant', pending: true, text: 'Editing the build…' }]);
    try {
      // The backend owns the codex prompt + the run-dir files; we send only the
      // job + the plain-English instruction and apply the revised HTML it returns.
      const res = await window.GooseAPI.assistEdit(jobId, req);
      const out = stripFences(res && res.html ? res.html : '');
      setMsgs(m => m.filter(x => !x.pending));
      if (out && out.indexOf('<') !== -1 && out.length > 120) {
        setHtml(out);
        if (onApplied) onApplied();
        setMsgs(m => [...m, { role: 'assistant', text: `Applied “${req}”. The render is updated — open the code editor to inspect the change.` }]);
      } else {
        setMsgs(m => [...m, { role: 'assistant', text: "That came back too short to apply cleanly. Try a smaller, more specific change." }]);
      }
    } catch (e) {
      setMsgs(m => m.filter(x => !x.pending).concat({ role: 'assistant', text: '⚠ The edit didn’t go through. ' + (e && e.message ? e.message : 'Try again, or edit the HTML directly above.') }));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={'chatdock' + (open ? ' open' : '')}>
      <button className="cd-handle" onClick={() => setOpen(o => !o)}>
        <span className="cd-spark"><I.bolt size={14} /></span>
        <b>AI build assistant</b>
        <span className="cd-sub">mention a change · edits the whole build</span>
        <span className="grow"></span>
        <I.chevron size={15} style={{ transform: open ? 'rotate(90deg)' : 'rotate(-90deg)', transition: 'transform .18s' }} />
      </button>
      {open && (
        <div className="cd-body">
          <div className="cd-msgs" ref={scroller}>
            {msgs.map((m, i) => (
              <div key={i} className={'cd-msg ' + m.role + (m.pending ? ' pending' : '')}>
                {m.role === 'assistant' && <span className="cd-av"><I.bolt size={12} /></span>}
                <div className="cd-bubble">{m.text}{m.pending && <span className="cd-typing"><span></span><span></span><span></span></span>}</div>
              </div>
            ))}
          </div>
          <div className="cd-input">
            <input value={input} placeholder="Describe a change to the build…" disabled={busy}
              onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(); }} />
            <button className="btn btn-primary btn-sm" disabled={busy || !input.trim()} onClick={() => send()} title="Send to fix model">
              {busy ? <span className="spinner"></span> : <I.send size={15} />}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

/* Shared on window (the house module pattern) — PY-12's AI-edit route reuses the SAME
   section mapper (derive + inject + map), so the two consumers can't drift. */
Object.assign(window, { CodeWorkspace, CodeEditor, LivePreview, ChatDock, buildEmailHtml, stripFences, deriveSections, injectSections, buildSectionMap, secLabel });
